/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Sun Jan 14 2024
 **/
/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
	ClipboardEvent,
	DependencyList,
	PropsWithChildren,
	ForwardedRef,
	forwardRef,
	useEffect,
	useState,
	useRef,
	useMemo
} from "react";

import ReactDOMServer from "react-dom/server";
import ReactDOM from "react-dom/client";
import { SerializedStyles, Theme, css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import ReactQuill, { ReactQuillProps } from "@rsupport/react-quill";
import { Mention } from "quill-mention";
import { MentionBlot } from "quill-mention";
import Quill from "quill";
import type { Range } from "quill";
import { Delta } from "quill/core";
import has from "lodash-es/has";

import "quill/dist/quill.snow.css";

import { _T } from "@rfice/language";
import { EventHandler, useEventHandlers } from "@rfice/eventhandler";
import { Utility } from "@/frameworks/libraries/utilities";
import { Column, TypographyCSS, Icon } from "@/frameworks/designStuff/components";
import { RegExp } from "@/frameworks/core/commons";
import { usePersistActions } from "@/frameworks/core/persistence";

import { Keys, useChatKeyboardBinding, ChatParser, QuillMention } from "./private";
import QuillToolbar from "./QuillToolbar";
import ChatMentionItem from "./ChatMentionItem";

Quill.register("formats/mention", MentionBlot);
Quill.register("modules/mention", Mention);

const LOGTAG = "#ChatEditor#";

export const DefaultChatEditorFormats = [
	"bold",
	"italic",
	// "underline",
	"strike",
	"list",
	"code-block",
	"code",
	"link",
	"blockquote",
	"mention"
];

export type Mentions = { id: string; value: string; profileImagePath?: string }[];

type EditorStyle = {
	toolbar?: SerializedStyles;
	mention?: SerializedStyles;
	mentionList?: SerializedStyles;
	mentionItem?: SerializedStyles;
	container?: SerializedStyles;
	editor?: SerializedStyles;
	formats?: SerializedStyles;
	typewriter?: SerializedStyles;
	font?: {
		strong?: SerializedStyles;
		strike?: SerializedStyles;
		em?: SerializedStyles;
		ul?: SerializedStyles;
		ol?: SerializedStyles;
		pre?: SerializedStyles;
	};
};

export type ChatEditorProps = Partial<{
	onMount: () => void;
	onUnmount: () => void;
	placeholder: string;
	modules: { [key: string]: any };
	formats: string[];
	showToolbar: boolean;
	atMentions: Mentions;
	hashMentions: Mentions;
	editor: ReactQuillProps;
	editorStyle: EditorStyle;
	dependencyList: DependencyList;
	isEmergency?: boolean;
	onLinkRequest: (submit: (href?: string) => void) => void;
	onChange: (value: string) => void;
	onEntered: () => void;
	onPasteFile?: (ev: ClipboardEvent) => void;
	roomID?: string;
	set_composition: (state: boolean) => void;
	variant?: ChatUI.Size;
}>;

export type ChatEditorRefType = {
	getRichText: () => ChatServices.Format2.Container.Richtext | null;
	getText: () => string | undefined;
	setText: (formatted: ChatServices.Format2.Container.Richtext) => void;
	getContents: (clear: boolean) => { richText: ChatServices.Format2.Container.Richtext | null; text?: string };
	clearContents: () => void;
	getEditor: () => ReturnType<ReactQuill["getEditor"]> | undefined;
	focus: () => void;
	blur: () => void;
};

type OptionTypes = {
	toolBarPinned: boolean;
};

import("quill/ui/icons").then((icons) => {
	icons.default["code-block"] = ReactDOMServer.renderToString(<Icon name="TextTypeCodeblock24" />);
});

const appendProtocol = (link: string): string => {
	if (!/^(http:\/\/|https:\/\/)/i.test(link)) {
		return `https://${link}`;
	}
	return link;
};

function ChatEditorComponent(
	{
		placeholder,
		modules,
		formats,
		atMentions,
		hashMentions,
		dependencyList,
		isEmergency,
		variant,
		...props
	}: PropsWithChildren<ChatEditorProps>,
	ref: ForwardedRef<ChatEditorRefType>
) {
	const theme = useTheme();
	const editor = useRef<ReactQuill>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const atMentionsRef = useRef(atMentions);
	const isComposition = useRef(false);
	const optionsRef = useRef<OptionTypes>({ toolBarPinned: true });
	const [isShowToolbar, setShowToolbar] = useState(props.showToolbar ?? optionsRef.current.toolBarPinned);
	const mentionModule = new QuillMention(editor.current);

	/**
	 * @description
	 * 컴포넌트 마운트 시 콜백 호출
	 * 컴포넌트 언마운트 시 콜백 호출
	 */
	useEffect(() => {
		props.onMount?.();
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		atMentionsRef.current = atMentions;
	}, [atMentions]);

	const _modules = useMemo(
		() => ({
			toolbar: { container: `#toolbar-${props.roomID ?? "NoRoomID"}` },
			keyboard: {
				bindings: {
					enter: {
						key: Keys.ENTER, // shift-enter는 줄 바꿈, enter는 전송임, 한글의 경우 완성하여 전송함
						handler: (range: Range, context: any) => {
							props.onEntered?.();
							placeholder && fn.update_placeholder(placeholder);
						}
					},
					highlightNextMentionItem: {
						key: Keys.DOWN,
						handler: () => {
							mentionModule.highlightNext();
							return true;
						}
					},
					highlightPrevMentionItem: {
						key: Keys.UP,
						handler: () => {
							mentionModule.highlightPrev();
							return true;
						}
					},
					openToolbar: {
						key: "T",
						shortKey: true,
						altKey: true,
						handler: () => {
							EventHandler.fire("$Editor::ToogleToolbar$");
						}
					},
					"list autofill": {
						key: " ",
						prefix: /^(\d+)\.$/,
						handler() {
							return true;
						}
					}
				}
			},
			mention: {
				allowedChars: /.*/s,
				mentionDenotationChars: ["@", "#"],
				listItemClass: "mention-list-item",
				showDenotationChar: true,
				renderItem: (item: { id: string; value: string; profileImagePath?: string }) => {
					const container = document.createElement("div");
					const root = ReactDOM.createRoot(container);
					root.render(<ChatMentionItem item={item} />);
					return container;
				},
				source: (
					searchTerm: string,
					renderList: (arg1: typeof atMentions, arg2: string) => void,
					mentionChar: string
				) => {
					//@Description
					//'영문 / 숫자 / 특수문자' 뒤에 바로 연결되어 입력된 경우 멘션창 노출되지 않도록 해당 요구사항이 존재하여 추가
					const isPrevCharValid = (text?: string) => {
						const regexList = [RegExp.ETC_TEXT_REG_FOR_CHAT, RegExp.ENG_TEXT_REG, RegExp.NUMBER_TEXT_REG];
						return text && text !== "@" && regexList.some((regex) => regex.test(text));
					};

					const values = mentionChar === "@" ? atMentionsRef.current : hashMentions;
					if (values) {
						const text = fn.editor?.getText();
						const prevAtText = text?.charAt(text.lastIndexOf("@") - 1);

						if (isPrevCharValid(prevAtText)) {
							renderList([], searchTerm);
							return;
						}

						if (searchTerm.length === 0) {
							renderList(values, searchTerm);
						} else {
							const matches = [];
							for (let i = 0; i < values.length; i++) {
								if (~values[i].value.toLowerCase().indexOf(searchTerm.trim().toLowerCase()))
									matches.push(values[i]);
							}
							renderList(matches, searchTerm);
						}
					}
				},
				onSelect: (
					item: DOMStringMap,
					insertItem: (
						data: Record<string, unknown>,
						programmaticInsert?: boolean,
						overriddenOptions?: object
					) => void
				) => {
					insertItem(item);
				}
			}
		}),
		[]
	);

	const fn = {
		get editor() {
			return editor.current?.getEditor();
		},
		value: {
			richtext_length: 0,
			get last_richtext_index() {
				return fn.value.richtext_length - 1;
			}
		},
		make_richtext() {
			return (editor.current && new ChatParser(editor.current).richtext()) || null;
		},
		get_rich_text() {
			return fn.make_richtext();
		},
		get_text() {
			return this.editor?.getText();
		},
		set_text(formatted: ChatServices.Format2.Container.Richtext) {
			editor.current && new ChatParser(editor.current).parse_to_delta(formatted);
			// console.debug("#ChatEditor# after set text", this.editor?.getContents());
		},
		get_contents(clear: boolean) {
			const result = { richText: fn.get_rich_text(), text: fn.get_text() };
			if (clear) {
				fn.clear_contents();
			}
			return result;
		},
		clear_contents() {
			this.editor?.setText("");
			!optionsRef.current.toolBarPinned && setShowToolbar(false);
		},
		set_focus() {
			editor.current?.focus();
		},
		set_blur() {
			editor.current?.blur();
		},
		update_placeholder(text: string) {
			const editorDOMRoot = document.querySelector(`#editor-${props.roomID ?? "NoRoomID"}`);
			const editorDOM = editorDOMRoot?.querySelector(".ql-editor");
			// console.debug(LOGTAG, "update_placeholder", text, editorDOM);
			editorDOM?.setAttribute("data-placeholder", text);
		}
	};

	useChatKeyboardBinding(editor.current);

	const interfaceRef = useRef<ChatEditorRefType>({
		getRichText: fn.get_rich_text,
		getText: fn.get_text,
		setText: fn.set_text,
		getContents: fn.get_contents,
		clearContents: fn.clear_contents,
		getEditor: () => fn.editor,
		focus: fn.set_focus,
		blur: fn.set_blur
	});

	const set_instance = (value: ChatEditorRefType | null) => {
		if (ref) {
			if (typeof ref === "function") {
				ref(value);
			} else {
				ref.current = value;
			}
		}
	};

	const setup_toolbar = () => {
		const toolbar = document.getElementById(`toolbar-${props.roomID ?? "NoRoomID"}`);
		const linkButton = toolbar?.querySelector(".ql-link") as HTMLButtonElement;

		linkButton.onclick = () => {
			const targetSelection = fn.editor?.getSelection(true);
			if (!targetSelection) return;

			if (props.onLinkRequest) {
				props.onLinkRequest((href) => {
					if (href) {
						let length = targetSelection.length;
						if (length === 0) {
							fn.editor?.insertText(targetSelection.index, href, "user");
							length = href.length;
						}
						fn.editor?.formatText(targetSelection.index, length, "link", href, "user");
						fn.editor?.setSelection(targetSelection.index + length, 0, "silent");
					}
				});
			} else {
				fn.editor?.removeFormat(targetSelection.index, targetSelection.length, "user");
			}
		};
	};

	useEffect(() => {
		set_instance(interfaceRef.current);
		setup_toolbar();

		return () => {
			handle_blur();
			set_instance(null);
		};
	}, []);

	useEventHandlers(
		{
			onEditorToogleToolbar() {
				optionsRef.current.toolBarPinned = !isShowToolbar;
				setShowToolbar(!isShowToolbar);
			},
			onEditorDraft(id: string) {
				if (props.roomID === id) {
					// console.debug(LOGTAG, "onEditorDraft", props.roomID, id);
					const text = fn.get_rich_text();
					props.roomID && text && chats.save_chat_draft(props.roomID, text);
				}
			}
		},
		[...(dependencyList ?? []), isShowToolbar]
	);

	const calc_offset_with_mention = () => {
		let offset = 0;
		const contents = fn.editor?.getContents();
		contents?.eachLine((line) => {
			for (const item of line.ops) {
				if (has(item.insert, "mention")) {
					offset += 1;
				}
			}
		});
		return offset;
	};

	const getCurrentLineContent = () => {
		if (fn.editor) {
			const selection = fn.editor.getSelection();
			if (!selection) return "";

			const lines = Array.from(fn.editor.getLines(selection.index - 1));
			if (lines.length === 0) return "";

			const line = lines[0];

			if (line.domNode instanceof Text) {
				return line.domNode.data;
			}

			if (line.domNode instanceof Element) {
				return line.domNode.textContent || "";
			}

			const lineLength = line.length();
			const lineIndex = fn.editor.getIndex(line);
			return fn.editor.getText(lineIndex, lineLength);
		}
	};

	const handle_text_change = (value: string, delta: Delta, source: string) => {
		const range = fn.editor?.getSelection();
		if (!range) return;

		const currentLine = getCurrentLineContent();

		if (typeof currentLine === "string" && currentLine?.startsWith("1. ")) {
			fn.editor?.deleteText(range.index - 3, 3);
			fn.editor?.formatLine(range.index - 3, range.length, "list", "ordered");
			setTimeout(() => {
				fn.editor?.setSelection(range.index - 3, 0, "silent");
			}, 0);
		}
		if (typeof currentLine === "string" && currentLine?.startsWith("- ")) {
			fn.editor?.deleteText(range.index - 2, 2);
			fn.editor?.formatLine(range.index - 2, range.length, "list", "bullet");
			setTimeout(() => {
				fn.editor?.setSelection(range.index - 2, 0, "silent");
			}, 0);
		}

		if (fn.editor && source === "user") {
			const selection = fn.editor.getSelection();
			if (selection) {
				const editorText = fn.editor.getText(0);

				const fragments = [...editorText.matchAll(RegExp.TEXT_URL_REG)].map((m) => ({
					fragment: m[0],
					index: m.index || 0
				}));

				const emailMatches = [...editorText.matchAll(RegExp.MAIL_REG)].map((m) => ({
					email: m[0],
					index: m.index || 0
				}));

				for (const { email, index } of emailMatches) {
					const range = {
						index: index + calc_offset_with_mention(),
						length: email.length
					};

					if (!fn.editor.getFormat(range)?.link) {
						fn.editor.formatText(range, "link", `mailto:${email}`, "silent");
						fn.editor.setSelection(selection.length + delta.length(), 0, "silent");
					}
				}

				for (const { fragment, index } of fragments) {
					const range = { index: index + calc_offset_with_mention(), length: fragment.length };

					if (!has(fn.editor.getFormat(range), "link") && Utility.isURL(fragment)) {
						fn.editor.formatText(range, "link", appendProtocol(fragment), "silent");
						fn.editor.setSelection(selection.length + delta.length(), 0, "silent");
					}
				}
			}
		}
		props.onChange?.(value);
	};

	const { chats } = usePersistActions();

	const handle_blur = () => {
		!optionsRef.current.toolBarPinned && setShowToolbar(false);
	};

	const handle_keydown = (ev: any) => {
		if (fn.editor) {
			const key = ev.key;
			const text = editor.current?.value;
			const range = fn.editor.getSelection();

			if (key === "Enter" && ev.shiftKey && range) {
				const currentPosition = range.index;
				const lastNewLineIndex = currentPosition - 1;
				const previousLineFormat = fn.editor.getFormat(lastNewLineIndex, 1);
				const currentLine = getCurrentLineContent();

				if (
					(previousLineFormat.list === "bullet" || previousLineFormat.list === "ordered") &&
					currentLine?.length === 0
				) {
					fn.editor.removeFormat(lastNewLineIndex, 1);
					fn.editor.deleteText(lastNewLineIndex, 1);
				}
			}

			if (key.length !== 1 && key !== "Backspace" && key !== "Shift") {
				return;
			}
			fn.update_placeholder(
				(text === "" || text === "<p><br></p>") && isComposition.current
					? ""
					: placeholder ?? _T("Enter your message")
			);

			if (key === "Enter") {
				if (range && range.length === 0) {
					ev.preventDefault();
					fn.editor.insertText(range.index, "\n");
					fn.editor.setSelection(range.index + 1);
				}
			}
		}
	};

	const handle_composition_before_start = (data: any[]) => {
		fn.update_placeholder("");
		isComposition.current = true;
		props.set_composition?.(true);
	};

	const handle_composition_before_end = (data: any[]) => {
		isComposition.current = false;
		props.set_composition?.(false);
	};
	const handle_editor_debug = (tag: string, message: string, data: any[]) => console.debug(tag, message, ...data);

	useEffect(() => {
		if (props.roomID) {
			const text = chats.chat_draft(props.roomID);
			if (text) {
				fn.set_text(text);
			}
		}
	}, [props.roomID]);

	return (
		<EditorContainer
			ref={containerRef}
			onPaste={(ev) => {
				if (ev.clipboardData.files.length === 0) return;
				ev.stopPropagation();
				props.onPasteFile?.(ev);
			}}
			style={{ borderTop: `${variant === "transparent" ? "0" : "1px solid rgba(50, 51, 56, 0.16)"}` }}
		>
			<QuillToolbar variant={variant} roomID={props.roomID ?? "NoRoomID"} />
			<ReactQuill
				id={props.roomID ?? "NoRoomID"}
				ref={editor}
				modules={{ ...(modules ?? _modules) }}
				theme="snow"
				css={QuillCSS({
					theme,
					isShowToolbar,
					editorStyle: props.editorStyle,
					isEmergency: isEmergency,
					// isFocus: miniChatValue?.focus,
					variant
				})}
				formats={formats ?? DefaultChatEditorFormats}
				placeholder={placeholder ?? _T("Enter your message")}
				onChange={handle_text_change}
				onKeyDown={handle_keydown}
				onBlur={handle_blur}
				onCompositionBeforeStart={handle_composition_before_start}
				onCompositionBeforeEnd={handle_composition_before_end}
				debug={handle_editor_debug}
				{...(props.editor ?? {})}
			/>
		</EditorContainer>
	);
}

const ChatEditor = forwardRef(ChatEditorComponent);
ChatEditor.displayName = "ChatEditorComponent";
export default ChatEditor;

const EditorContainer = styled(Column)`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
enum ChatEditorVariant {
	FULL_SIZE = "fullSize",
	MINI_POPUP = "miniPopup"
}

const QuillCSS = ({
	theme,
	isShowToolbar,
	editorStyle,
	isEmergency,
	// isFocus,
	variant
}: {
	theme: Theme;
	isShowToolbar: boolean;
	editorStyle?: EditorStyle;
	isEmergency?: boolean;
	// isFocus?: boolean;
	variant?: ChatUI.Size;
}) => css`
	background-color: ${theme.colors.neutral_00_100};
	position: relative;
	z-index: 1;
	width: -webkit-fill-available;
	height: 100%;
	border-top: 0 !important;
	border-radius: 0px !important;
	${editorStyle?.editor};

	.ql-toolbar.ql-snow {
		display: ${!isShowToolbar && "none"};
		${editorStyle?.toolbar};
		padding: ${variant === ChatEditorVariant.FULL_SIZE
			? "4px 8px;"
			: variant === ChatEditorVariant.MINI_POPUP
				? "4px 8px;"
				: "8px 8px 10px 8px;"};

		.ql-formats {
			${editorStyle?.formats};
			gap: ${variant === ChatEditorVariant.FULL_SIZE
				? "8px;"
				: variant === ChatEditorVariant.MINI_POPUP
					? "4px;"
					: "4px;"};

			button {
				display: flex;
				justify-content: center;
				align-items: center;
				width: ${variant === ChatEditorVariant.FULL_SIZE
					? "28px;"
					: variant === ChatEditorVariant.MINI_POPUP
						? "28px;"
						: "24px;"};
				height: ${variant === ChatEditorVariant.FULL_SIZE
					? "28px;"
					: variant === ChatEditorVariant.MINI_POPUP
						? "28px;"
						: "24px;"};
			}
		}

		// 버튼 그룹 간격
		.ql-formats:not(:last-of-type) {
			margin-right: 1px;
			border-right: solid 1px #ccc;
		}
	}
	.ql-container.ql-snow {
		border: 0;
		height: auto;
		${editorStyle?.container};
	}
	.mention {
		${TypographyCSS("Title2", "accent_40_100")}
		${editorStyle?.mention};
	}
	.ql-mention-list {
		padding: 8px;
		border-radius: 8px;
		background-color: #fff;
		box-shadow:
			0px 8px 16px 0px rgba(0, 0, 0, 0.2),
			0px 0px 4px 0px rgba(0, 0, 0, 0.04);
		${editorStyle?.mentionList};
	}
	.mention-list-item {
		${editorStyle?.mentionItem};
	}
	.ql-editor {
		${TypographyCSS("Body3", "neutral_60_100")};
		${editorStyle?.typewriter};
		padding: 8px 16px;
		margin: 0 8px;
		border-radius: 8px;
		${TypographyCSS("Body3", "neutral_80_100")};
		padding: 8px;
		color: ${isEmergency ? theme.colors.warning_80_100 : "auto"};
		&:focus {
			background-color: ${isEmergency ? theme.colors.warning_00_100 : theme.colors.accent_00_100};
		}
		strong {
			font-weight: bold;
			${editorStyle?.font?.strong};
		}
		strike {
			font-style: strike;
			${editorStyle?.font?.strike};
		}
		em {
			font-style: italic;
			${editorStyle?.font?.em};
		}
		.ql-code-block-container {
			border-radius: 6px;
			padding: 6px 10px 6px 10px;
			font-family: "Fira Code", "Courier New", Courier, monospace !important;
			font-size: 13px;
			background-color: #f5f5f5;
			border: solid 1px #ddd;
			color: #0f0f0f;
			${editorStyle?.font?.pre};
		}
	}
	.ql-editor ol li:not(.ql-direction-rtl),
	.ql-editor ul li:not(.ql-direction-rtl) {
		padding-left: 0;
	}
	.ql-editor p {
		font-family: "Pretendard", "Noto Color Emoji" !important;
	}
`;
